import React from 'react';
import {
  getHeroContent, HeroImage, StoreLogo, CartBtn, TabContent, StoreFooter, FaHeart, getTabLabel, InlineEditableText, AWS_URL,MdStore, MdList, MdSearch, MdFilterList
} from './ThemeLayoutHelpers';


export default function SenseLayout({ p }) {
  const T = p.storeTheme || {};
  const primary = T.primaryColor || "#7c3aed";
  const secondary = T.secondaryColor || "#a78bfa";
  const text = p.storefrontText || {};
  const featured = (p.allProducts || []).slice(0, 4);
  const copy = getHeroContent(p, {
    eyebrow: "Sense Theme",
    title: "Editorial design for bold brands.",
    subtitle: "Large visual blocks, strong contrast, and storytelling flow to make products feel premium.",
    primaryCta: "Browse Products",
    secondaryCta: "Store Story",
  });

  const navClass = p.storefrontPreset?.navClass || "bg-zinc-950 text-white shadow-xl";
  const isAtelier = p.resolvedThemeId === "atelier";

  return (
    <div className="min-h-screen flex flex-col" style={{ ...p.themeStyles, fontFamily: T.fontFamily || "Space Grotesk, ui-sans-serif, system-ui", backgroundColor: T.backgroundColor || "#fafafa" }}>
      <nav className={`w-full sticky top-0 z-40 ${navClass}`}>
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          <StoreLogo storeInfo={p.storeInfo} size="h-10 w-10" border="border-2 border-white/20" />
          <span className="font-bold text-base md:text-lg tracking-wide">{p.storeInfo?.businessName}</span>
          <div className="hidden md:block flex-1 max-w-sm ml-4">
            <div className="relative">
              <input
                value={p.searchQuery}
                onChange={(e) => p.setSearchQuery(e.target.value)}
                placeholder={text.searchProducts || "Search products"}
                className="w-full py-2 pl-10 pr-4 rounded-xl border bg-white/10 text-white placeholder-white/50 text-sm focus:outline-none focus:ring-2 focus:ring-white/30"
                style={{ fontFamily: T.fontFamily || "Space Grotesk, ui-sans-serif, system-ui", borderColor: `${primary}40` }}
              />
              <MdSearch className="absolute left-3 top-2.5 text-lg text-white/50" />
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={() => p.setFilterPanelOpen(true)}
              className="p-2.5 rounded-xl bg-white/10 border border-white/25 hover:bg-white/20 transition-all"
              title={text.filter || "Filter"}
            >
              <MdFilterList className="text-xl text-white" />
            </button>
            <button
              onClick={p.handleGoToWishlist}
              className="relative p-2.5 rounded-xl bg-white/10 border border-white/25 hover:bg-white/20 transition-all"
              title={text.wishlist || "Wishlist"}
            >
              <FaHeart className="text-lg" />
              {p.wishlistCount > 0 && <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full text-[9px] bg-rose-500 text-white flex items-center justify-center">{p.wishlistCount}</span>}
            </button>
            <CartBtn p={p} dark />
            {p.userMenuElement}
          </div>
        </div>
      </nav>

      <section className="max-w-7xl mx-auto w-full px-4 pt-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          <div className="lg:col-span-5 rounded-3xl p-7 md:p-10 text-white relative overflow-hidden shadow-[0_24px_70px_rgba(0,0,0,0.15)]" style={{ background: `linear-gradient(145deg, ${primary} 0%, ${secondary} 100%)` }}>
            <div className="absolute -right-14 -top-14 h-40 w-40 rounded-full bg-white/15" />
            <div className="absolute -left-12 -bottom-12 h-36 w-36 rounded-full bg-black/12" />
            <p className="text-xs uppercase tracking-[0.24em] text-white/70 font-semibold"><InlineEditableText p={p} field="heroEyebrow" value={copy.eyebrow}>{copy.eyebrow}</InlineEditableText></p>
            <h1 className="text-3xl md:text-4xl font-extrabold leading-tight mt-3 drop-shadow-sm"><InlineEditableText p={p} field="heroTitle" value={copy.title}>{copy.title}</InlineEditableText></h1>
            <p className="mt-3 text-sm text-white/85 max-w-md"><InlineEditableText p={p} field="heroSubtitle" value={copy.subtitle}>{copy.subtitle}</InlineEditableText></p>
            <div className="mt-6 flex gap-3">
              <button onClick={() => p.setActiveTab("products")} className="px-5 py-2.5 rounded-xl text-sm font-bold bg-black/25 border border-white/35 hover:bg-black/40 transition-colors"><InlineEditableText p={p} field="primaryCtaText" value={copy.primaryCta}>{copy.primaryCta}</InlineEditableText></button>
              <button onClick={() => p.setActiveTab("details")} className="px-5 py-2.5 rounded-xl text-sm font-semibold bg-white text-zinc-900 hover:bg-zinc-100 transition-colors"><InlineEditableText p={p} field="secondaryCtaText" value={copy.secondaryCta}>{copy.secondaryCta}</InlineEditableText></button>
            </div>
          </div>

          <div className="lg:col-span-7 rounded-3xl border overflow-hidden shadow-[0_22px_60px_rgba(0,0,0,0.1)]" style={{ borderColor: `${primary}20` }}>
            <HeroImage p={p} className="h-80 md:h-[26rem]">
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent pointer-events-none" />
              <div className="absolute inset-x-0 bottom-0 p-7 text-white">
                <p className="text-3xl font-extrabold mt-1 tracking-tight">{p.storeInfo?.businessName || copy.title}</p>
                <p className="text-sm text-white/70 mt-1">{p.storeInfo?.businessCategory || ""}</p>
              </div>
            </HeroImage>
          </div>
        </div>
      </section>

      {!isAtelier && featured.length > 0 && (
        <section className="max-w-7xl mx-auto w-full px-4 mt-6">
          <div className="rounded-3xl border bg-white p-5 md:p-6 shadow-sm" style={{ borderColor: `${primary}15` }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-extrabold uppercase tracking-[0.18em]" style={{ color: primary }}>{text.products || "Featured Products"}</h2>
              <button onClick={() => p.setActiveTab("products")} className="text-xs font-semibold" style={{ color: primary }}>{text.viewAll || "View all"}</button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {featured.map((prod) => (
                <button key={prod._id} onClick={() => p.handleAddToCart(prod)} className="text-left rounded-2xl border hover:shadow-md transition-all overflow-hidden" style={{ borderColor: `${primary}10`, backgroundColor: `${primary}05` }}>
                  <div className="h-24 md:h-28 overflow-hidden bg-white/50">
                    {prod.productImage || prod.thumbnail ? (
                      <img src={(prod.productImage || prod.thumbnail).startsWith('http') ? (prod.productImage || prod.thumbnail) : AWS_URL + (prod.productImage || prod.thumbnail)} alt={prod.productName} className="h-full w-full object-cover" />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center text-xs text-gray-400">Product</div>
                    )}
                  </div>
                  <div className="p-3">
                    <p className="text-xs font-semibold text-slate-800 line-clamp-1">{prod.productName}</p>
                    <p className="text-sm font-bold mt-1" style={{ color: primary }}>₹{prod.salePrice}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto w-full px-4 mt-8">
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          {[
            ["products", getTabLabel(p, "products"), <MdList key="p" />],
            ["services", getTabLabel(p, "services"), <MdList key="s" />],
            ["details", getTabLabel(p, "details"), <MdStore key="d" />],
          ].map(([key, label, icon]) => (
            <button
              key={key}
              onClick={() => p.setActiveTab(key)}
              className="px-6 py-3 rounded-full text-sm font-bold flex items-center gap-2 border transition-all whitespace-nowrap shadow-sm"
              style={
                p.activeTab === key
                  ? { backgroundColor: primary, color: "#fff", borderColor: primary }
                  : { backgroundColor: "#fff", color: primary, borderColor: `${primary}30` }
              }
            >
              {icon} {label}
            </button>
          ))}
        </div>
      </section>

      <section className="flex-1 max-w-7xl mx-auto w-full px-4 py-6">
        <TabContent p={p} />
      </section>

      <StoreFooter p={p} />
    </div>
  );
}

// ─── Empire ─────────────────────────────────────────────────────────────────